import type { FormConfig, PropertySchema } from '@takeshape/schema';
import { getNamedToolSchema } from '@takeshape/schema/project-schema/latest.js';
import DropdownWidget from 'app/common/components/widgets/dropdown.js';
import GraphQLWidget from 'app/common/components/widgets/graphql';
import SelectQueryWidget from 'app/common/components/widgets/select-query';
import SingleLineTextWidget from 'app/common/components/widgets/single-line-text';
import type { ImmutableMap } from 'app/common/utils/immutable-types';
import type { SchemaFormMainOwnProps, SchemaFormOwnProps } from 'app/schema-form/components/schema-form/schema-form';
import type { ErrorList, Validator } from 'app/schema-form/types';
import { fromJS } from 'immutable';
import cloneDeep from 'lodash/cloneDeep.js';
import type React from 'react';
import { TOOL_FORM_NAME } from '../../constants.js';
import type { ToolEditorFormState } from '../../types.js';
import ToolArgumentPicker from './tool-editor-argument-picker/index.js';
import { ToolArgumentPreview } from './tool-editor-argument-preview.js';

const baseToolSchema = getNamedToolSchema();
const toolSchema = {
  ...baseToolSchema,
  definitions: {
    ...baseToolSchema.definitions,

    // TODO remove override and support full query configuration
    toolExecution: {
      title: 'ToolExecutionGraphQLJSON',
      type: 'object',
      properties: {
        type: {
          type: 'string'
        },
        query: {
          $ref: '#/definitions/toolQueryRefConfig'
        }
      },
      additionalProperties: false,
      required: ['query']
    }
  }
};

export function createFormConfig(
  additionalConfig?: Partial<SchemaFormOwnProps<ToolEditorFormState>>
): SchemaFormMainOwnProps<ToolEditorFormState> {
  const schema = cloneDeep(toolSchema) as PropertySchema;

  const formConfig: FormConfig = {
    order: ['name', 'visibility', 'description', 'execution'],
    wrapper: 'noWrapper',
    properties: {
      name: {
        widget: 'singleLineText'
      },
      visibility: {
        widget: 'dropdown',
        instructions: '"public" tools will be made available via MCP'
      },
      description: {
        widget: 'singleLineText',
        instructions: 'Used by the language model to determine whether to use the tool.'
      }
    }
  };

  const forms: Record<string, FormConfig> = {
    toolQueryRefConfig: {
      default: {
        wrapper: 'noWrapper',
        order: ['ref', 'args', 'selectionSet'],
        properties: {
          ref: {
            widget: 'selectQuery',
            title: 'Query or Mutation',
            includeMutations: true
          },
          args: {
            title: 'Arguments',
            // preview: 'toolArgumentPreview',
            collapseByDefault: true,
            items: {
              widget: 'toolArgumentPicker'
            }
          },
          selectionSet: {
            title: 'Selection Set',
            widget: 'graphql'
          }
        }
      }
    },
    toolExecution: {
      default: {
        wrapper: 'noWrapper',
        order: ['query']
      }
    },
    agentToolConfigArgAgent: {
      default: {
        properties: {
          argName: {
            widget: 'toolArgumentPicker'
          }
        }
      }
    }
  };

  return {
    formName: TOOL_FORM_NAME,
    schema,
    config: fromJS(formConfig) as ImmutableMap<FormConfig>,
    forms: fromJS(forms),
    ...additionalConfig,
    partial: false
  };
}

/**
 * Should be created independently of the form config to avoid having the form config shift when the working copy changes.
 */
export function createFormValidator(toolName?: string, existingToolNames: string[] = []): Validator {
  return (values: Partial<ToolEditorFormState>) => {
    const errors: ErrorList = {};

    if (values.name && values.name !== toolName && existingToolNames.includes(values.name)) {
      errors.name = 'Tool name must be unique.';
    }

    return errors;
  };
}

export function createFormWidgets() {
  return {
    noWrapper: ({ children }: { children: React.ReactNode }) => children,
    singleLineText: SingleLineTextWidget,
    dropdown: DropdownWidget,
    selectQuery: SelectQueryWidget,
    graphql: GraphQLWidget,
    toolArgumentPreview: ToolArgumentPreview,
    toolArgumentPicker: ToolArgumentPicker
  };
}
